import { useState, useCallback, useMemo } from 'react';
import { journalAPI } from '@/lib/api';
import { processTemplateVariables } from '@/lib/templateVariables';
import { toast } from 'sonner';

/**
 * Journal-specific actions: notebook management, entry creation, filtered list.
 */
export function useJournalActions({ notebooks, templates, user, createNote, fetchNotebooks, setActiveView, notes }) {
  const [journalNotebookId, setJournalNotebookId] = useState(null);

  const ensureJournalNotebook = useCallback(async () => {
    if (journalNotebookId) return journalNotebookId;
    try {
      const res = await journalAPI.ensureNotebook();
      const nbId = res.data.id;
      setJournalNotebookId(nbId);
      await fetchNotebooks();
      return nbId;
    } catch {
      toast.error('Failed to create journal notebook');
      return null;
    }
  }, [journalNotebookId, fetchNotebooks]);

  const createJournalEntry = useCallback(async (templateId = null) => {
    try {
      const nbId = await ensureJournalNotebook();
      if (!nbId) return null;

      const now = new Date();
      const entryDate = now.toISOString().slice(0, 10);
      const timeStr = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      const dateStr = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      const autoTitle = `${dateStr} at ${timeStr}`;

      let content = '';
      if (templateId) {
        const tmpl = templates.find((t) => t.id === templateId);
        if (tmpl?.content) {
          content = processTemplateVariables(tmpl.content, { title: autoTitle, user: user?.email || '' });
        }
      } else {
        const journalNb = notebooks.find((n) => n.id === nbId);
        if (journalNb?.default_template_id) {
          const tmpl = templates.find((t) => t.id === journalNb.default_template_id);
          if (tmpl?.content) {
            content = processTemplateVariables(tmpl.content, { title: autoTitle, user: user?.email || '' });
          }
        }
      }

      const note = await createNote({
        title: autoTitle,
        content,
        notebook_id: nbId,
        is_journal: true,
        entry_date: entryDate,
      });

      setActiveView('journal');
      return note;
    } catch {
      toast.error('Failed to create journal entry');
      return null;
    }
  }, [ensureJournalNotebook, templates, notebooks, createNote, user?.email, setActiveView]);

  const journalNotes = useMemo(() => {
    return notes
      .filter((n) => n.is_journal && !n.is_deleted && !n.is_archived)
      .sort((a, b) => {
        const dateCompare = (b.entry_date || '').localeCompare(a.entry_date || '');
        if (dateCompare !== 0) return dateCompare;
        return new Date(b.created_at) - new Date(a.created_at);
      });
  }, [notes]);

  return { journalNotebookId, setJournalNotebookId, ensureJournalNotebook, createJournalEntry, journalNotes };
}
